import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Building2,
  ChevronDown,
  ChevronRight,
  Loader2,
  MoreHorizontal,
  Network,
  Plus,
  ShieldAlert,
  Users,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/useAuthStore";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useOrgTree, useOrgMutation } from "@/hooks/useAdminUsers";
import type { OrgDepartment, OrgTeam, OrgTreeResponse } from "@/api/adminCenter";

/**
 * D-044 S2 — #3 Company structure / Org-tree (VISUAL v1.1: accordion 2 рівні,
 * 1:1 D-044c, MP1 type-badge, MP2 supervisor≠head, MP7 gated+audit, MP10
 * move=drawer-select, NO drag). Empty-стани §0 — кожен з живим CTA.
 */

export function AdminStructurePage() {
  const perms = useAuthStore((s) => s.permissions);
  const canView = !!perms?.canViewAdminCenter;
  const treeQ = useOrgTree(canView);

  const [openDepts, setOpenDepts] = useState<Record<string, boolean>>({});
  const [openTeams, setOpenTeams] = useState<Record<string, boolean>>({});
  const [teamDrawer, setTeamDrawer] = useState<OrgTeam | null>(null);
  const [createDept, setCreateDept] = useState(false);
  const [createTeam, setCreateTeam] = useState<string | null | false>(false); // deptId | null(unassigned) | false
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const showToast = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    window.setTimeout(() => setToast(null), 4000);
  };

  if (!canView) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[640px] rounded-lg border bg-muted/30 p-8 text-center">
          <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
          <p className="text-sm font-medium">Немає доступу до Адмін-центру</p>
          <p className="mt-1 text-xs text-muted-foreground">Розділ доступний лише адміністраторам.</p>
        </div>
      </div>
    );
  }

  const data = treeQ.data;
  const allowed = data?.allowedActions ?? {};

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1100px] space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
              <Network className="h-5 w-5" /> Структура компанії
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Відділ → команди → учасники · команда → supervisor (A-lite, 1:1).
            </p>
          </div>
          <div className="flex gap-2">
            {/* MP7: allowedActions-gated */}
            {allowed.addDepartment && (
              <Button variant="outline" size="sm" onClick={() => setCreateDept(true)}>
                <Plus className="mr-1 h-4 w-4" /> Відділ
              </Button>
            )}
            {allowed.addTeam && (
              <Button size="sm" onClick={() => setCreateTeam(null)}>
                <Plus className="mr-1 h-4 w-4" /> Команда
              </Button>
            )}
          </div>
        </div>

        <div className="rounded-card border bg-card">
          {treeQ.isLoading && (
            <div className="space-y-2 p-4">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-11 w-full" />
              ))}
            </div>
          )}
          {treeQ.isError && (
            <div className="p-4 text-sm text-destructive">Не вдалося завантажити структуру.</div>
          )}
          {data && (
            <div className="divide-y">
              {data.departments.map((d) => (
                <DepartmentNode
                  key={d.id}
                  dept={d}
                  open={!!openDepts[d.id]}
                  onToggle={() => setOpenDepts((s) => ({ ...s, [d.id]: !s[d.id] }))}
                  openTeams={openTeams}
                  onToggleTeam={(tid) => setOpenTeams((s) => ({ ...s, [tid]: !s[tid] }))}
                  onTeamMenu={setTeamDrawer}
                  onAddTeam={allowed.addTeam ? () => setCreateTeam(d.id) : undefined}
                />
              ))}

              {data.unassignedTeams.length > 0 && (
                <div className="px-4 py-3">
                  <div className="mb-1 flex items-center gap-2 text-sm">
                    <AlertTriangle className="h-4 w-4 text-warning-foreground" />
                    <span className="font-medium">Команди без відділу</span>
                    <span className="text-xs text-muted-foreground">
                      привʼяжіть до відділу (1:1) через меню команди
                    </span>
                  </div>
                  {data.unassignedTeams.map((t) => (
                    <TeamRow
                      key={t.id}
                      team={t}
                      open={!!openTeams[t.id]}
                      onToggle={() => setOpenTeams((s) => ({ ...s, [t.id]: !s[t.id] }))}
                      onMenu={() => setTeamDrawer(t)}
                    />
                  ))}
                </div>
              )}

              {data.departments.length === 0 && data.unassignedTeams.length === 0 && (
                <div className="p-10 text-center text-sm text-muted-foreground">
                  Структура порожня.
                  <Button variant="outline" size="sm" className="ml-2" onClick={() => setCreateDept(true)}>
                    <Plus className="mr-1 h-4 w-4" /> Створити відділ
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* MP2 microcopy */}
        <p className="text-xs text-muted-foreground">
          Head відділу (functional: assign/escalation заявок) налаштовується у «Відділи та маршрути»
          (#6). Тут — supervisor команди (sales/team scope).
        </p>
      </div>

      {teamDrawer && data && (
        <TeamDrawer
          team={teamDrawer}
          tree={data}
          onClose={() => setTeamDrawer(null)}
          onToast={showToast}
        />
      )}
      {createDept && <CreateDeptDialog onClose={() => setCreateDept(false)} onToast={showToast} />}
      {createTeam !== false && data && (
        <CreateTeamDialog
          departments={data.departments}
          presetDeptId={createTeam}
          onClose={() => setCreateTeam(false)}
          onToast={showToast}
        />
      )}

      {toast && (
        <div
          role="status"
          className={cn(
            "fixed bottom-4 right-4 z-50 rounded-lg px-4 py-2.5 text-sm shadow-lg",
            toast.kind === "ok"
              ? "bg-success text-success-foreground"
              : "bg-destructive-soft text-destructive",
          )}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── Tree nodes ──────────────────────────────────────────────────────────────

function DepartmentNode({
  dept,
  open,
  onToggle,
  openTeams,
  onToggleTeam,
  onTeamMenu,
  onAddTeam,
}: {
  dept: OrgDepartment;
  open: boolean;
  onToggle: () => void;
  openTeams: Record<string, boolean>;
  onToggleTeam: (id: string) => void;
  onTeamMenu: (t: OrgTeam) => void;
  onAddTeam?: () => void;
}) {
  const memberCount = dept.teams.reduce((a, t) => a + t.members.length, 0);
  return (
    <div className="px-4 py-2.5">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left hover:bg-accent/50"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <Building2 className="h-4 w-4" />
        <span className="flex-1 text-sm">{dept.name}</span>
        {/* MP1: department-type badge */}
        {dept.orgType === "sales_scope" ? (
          <span
            className="rounded-badge bg-info px-2 py-0.5 text-xs text-info-foreground"
            title="Впливає на client-visibility (sales-scope)"
          >
            sales-scope
          </span>
        ) : (
          <span
            className="rounded-badge bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            title="Впливає на routing заявок (functional-inbox)"
          >
            functional-inbox
          </span>
        )}
        <span className="rounded-badge bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
          {dept.teams.length} ком. · {memberCount} уч.
        </span>
      </button>

      {open && (
        <div className="ml-6 mt-1.5 space-y-1">
          {dept.teams.length === 0 ? (
            <div className="flex items-center gap-2 py-1.5">
              <span className="inline-flex items-center gap-1 rounded-badge bg-warning px-2 py-0.5 text-xs text-warning-foreground">
                <AlertTriangle className="h-3 w-3" /> Команд немає
              </span>
              {onAddTeam && (
                <Button variant="outline" size="sm" onClick={onAddTeam}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> Команда
                </Button>
              )}
            </div>
          ) : (
            dept.teams.map((t) => (
              <TeamRow
                key={t.id}
                team={t}
                open={!!openTeams[t.id]}
                onToggle={() => onToggleTeam(t.id)}
                onMenu={() => onTeamMenu(t)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function TeamRow({
  team,
  open,
  onToggle,
  onMenu,
}: {
  team: OrgTeam;
  open: boolean;
  onToggle: () => void;
  onMenu: () => void;
}) {
  return (
    <div className="rounded-lg border px-2.5 py-1.5">
      <div className="flex items-center gap-2">
        <button type="button" onClick={onToggle} aria-expanded={open} aria-label={`Розгорнути ${team.name}`}>
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
        <Users className="h-4 w-4" />
        <span className="flex-1 text-sm">{team.name}</span>
        {team.supervisorName ? (
          <span className="text-xs text-muted-foreground">supervisor: {team.supervisorName}</span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-badge bg-warning px-2 py-0.5 text-xs text-warning-foreground">
            <AlertTriangle className="h-3 w-3" /> Без supervisor
          </span>
        )}
        <span className="rounded-badge bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
          {team.members.length} уч.
        </span>
        <Button variant="ghost" size="sm" aria-label={`Дії команди ${team.name}`} onClick={onMenu}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </div>
      {open && (
        <div className="ml-6 flex flex-wrap items-center gap-1.5 py-1.5">
          {team.members.length === 0 ? (
            <span className="text-xs text-muted-foreground">Учасників немає</span>
          ) : (
            team.members.map((m) => (
              <span
                key={m.id}
                className={cn(
                  "rounded-badge bg-muted px-2 py-0.5 text-xs",
                  m.isActive ? "text-foreground" : "text-muted-foreground line-through",
                )}
              >
                {m.name}
              </span>
            ))
          )}
          <Button variant="outline" size="sm" onClick={onMenu}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Учасник
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Team drawer (⋯): move-dept / supervisor / members / rename ─────────────

function TeamDrawer({
  team,
  tree,
  onClose,
  onToast,
}: {
  team: OrgTeam;
  tree: OrgTreeResponse;
  onClose: () => void;
  onToast: (k: "ok" | "err", m: string) => void;
}) {
  const panelRef = useDrawerA11y(true, onClose);
  const [section, setSection] = useState<"menu" | "move" | "supervisor" | "add" | "moveMember" | "rename">("menu");
  const [deptSel, setDeptSel] = useState<string | null>(team.departmentId);
  const [supSel, setSupSel] = useState<string | null>(team.supervisorId);
  const [userSel, setUserSel] = useState<string | null>(null);
  const [memberSel, setMemberSel] = useState<string | null>(null);
  const [toTeamSel, setToTeamSel] = useState<string | null>(null);
  const [name, setName] = useState(team.name);

  const moveTeamM = useOrgMutation("moveTeam");
  const supM = useOrgMutation("setSupervisor");
  const addM = useOrgMutation("addMember");
  const moveMemberM = useOrgMutation("moveMember");
  const renameM = useOrgMutation("renameTeam");

  const busy =
    moveTeamM.isPending || supM.isPending || addM.isPending || moveMemberM.isPending || renameM.isPending;

  const allTeams = useMemo(
    () => [...tree.departments.flatMap((d) => d.teams), ...tree.unassignedTeams],
    [tree],
  );
  const nonMembers = tree.userOptions.filter((u) => !team.members.some((m) => m.id === u.id));

  const done = (msg: string) => {
    onToast("ok", msg);
    onClose();
  };
  const fail = (e: unknown) => onToast("err", e instanceof Error ? e.message : "Помилка");

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Команда ${team.name}`}
        className="absolute right-0 top-0 h-full w-[380px] overflow-y-auto border-l bg-card p-5 shadow-xl outline-none"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{team.name}</h2>
          <Button variant="ghost" size="sm" aria-label="Закрити" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {section === "menu" && (
          <div className="flex flex-col gap-2">
            <Button variant="outline" size="sm" onClick={() => setSection("move")}>
              Перепривʼязати до відділу
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSection("supervisor")}>
              Призначити supervisor
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSection("add")}>
              Додати учасника
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={team.members.length === 0}
              onClick={() => setSection("moveMember")}
            >
              Перемістити учасника
            </Button>
            {team.members.length === 0 && (
              <p className="text-xs text-muted-foreground">Переміщення недоступне — у команді немає учасників.</p>
            )}
            <Button variant="outline" size="sm" onClick={() => setSection("rename")}>
              Перейменувати
            </Button>
          </div>
        )}

        {section === "move" && (
          <>
            {/* MP7: impact-warning */}
            <div className="mb-3 flex items-start gap-2 rounded-lg bg-warning px-3 py-2.5 text-xs leading-relaxed text-warning-foreground">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Перепривʼязка відділу впливає на: маршрути заявок відділу · scope-розрахунки
                команди · supervisor-звʼязки. Дію буде записано в аудит.
              </span>
            </div>
            <div className="space-y-1.5">
              {tree.departments.map((d) => (
                <SelRow key={d.id} label={`${d.name} (${d.orgType === "sales_scope" ? "sales" : "functional"})`} selected={deptSel === d.id} onClick={() => setDeptSel(d.id)} />
              ))}
              <SelRow label="Без відділу" selected={deptSel === null} onClick={() => setDeptSel(null)} />
            </div>
            <DrawerSave
              busy={busy}
              onCancel={() => setSection("menu")}
              onSave={() =>
                moveTeamM.mutate(
                  { teamId: team.id, departmentId: deptSel },
                  { onSuccess: () => done("Відділ команди оновлено"), onError: fail },
                )
              }
            />
          </>
        )}

        {section === "supervisor" && (
          <>
            <p className="mb-2 text-xs text-muted-foreground">
              Supervisor команди = sales/team scope (НЕ head відділу — той у #6).
            </p>
            <div className="max-h-[50vh] space-y-1.5 overflow-y-auto">
              {tree.userOptions.map((u) => (
                <SelRow key={u.id} label={u.name} selected={supSel === u.id} onClick={() => setSupSel(u.id)} />
              ))}
              <SelRow label="Зняти supervisor" selected={supSel === null} onClick={() => setSupSel(null)} />
            </div>
            <DrawerSave
              busy={busy}
              onCancel={() => setSection("menu")}
              onSave={() =>
                supM.mutate(
                  { teamId: team.id, userId: supSel },
                  { onSuccess: () => done("Supervisor оновлено"), onError: fail },
                )
              }
            />
          </>
        )}

        {section === "add" && (
          <>
            <p className="mb-2 text-xs text-muted-foreground">Додати учасника в «{team.name}»</p>
            <div className="max-h-[50vh] space-y-1.5 overflow-y-auto">
              {nonMembers.map((u) => (
                <SelRow key={u.id} label={u.name} selected={userSel === u.id} onClick={() => setUserSel(u.id)} />
              ))}
              {nonMembers.length === 0 && (
                <p className="text-xs text-muted-foreground">Усі активні користувачі вже в команді.</p>
              )}
            </div>
            <DrawerSave
              busy={busy}
              disabledReason={userSel === null ? "Оберіть користувача" : null}
              onCancel={() => setSection("menu")}
              onSave={() =>
                addM.mutate(
                  { teamId: team.id, userId: userSel! },
                  { onSuccess: () => done("Учасника додано"), onError: fail },
                )
              }
            />
          </>
        )}

        {section === "moveMember" && (
          <>
            <p className="mb-2 text-xs text-muted-foreground">Кого перемістити з «{team.name}»:</p>
            <div className="space-y-1.5">
              {team.members.map((m) => (
                <SelRow key={m.id} label={m.name} selected={memberSel === m.id} onClick={() => setMemberSel(m.id)} />
              ))}
            </div>
            <p className="mb-2 mt-3 text-xs text-muted-foreground">Куди:</p>
            <div className="max-h-[35vh] space-y-1.5 overflow-y-auto">
              {allTeams
                .filter((t) => t.id !== team.id)
                .map((t) => (
                  <SelRow key={t.id} label={t.name} selected={toTeamSel === t.id} onClick={() => setToTeamSel(t.id)} />
                ))}
            </div>
            <DrawerSave
              busy={busy}
              disabledReason={
                memberSel === null ? "Оберіть учасника" : toTeamSel === null ? "Оберіть цільову команду" : null
              }
              onCancel={() => setSection("menu")}
              onSave={() =>
                moveMemberM.mutate(
                  { userId: memberSel!, fromTeamId: team.id, toTeamId: toTeamSel! },
                  { onSuccess: () => done("Учасника переміщено"), onError: fail },
                )
              }
            />
          </>
        )}

        {section === "rename" && (
          <>
            <label className="block">
              <span className="mb-1 block text-xs text-muted-foreground">Назва команди</span>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <DrawerSave
              busy={busy}
              disabledReason={name.trim().length < 2 ? "Вкажіть назву" : null}
              onCancel={() => setSection("menu")}
              onSave={() =>
                renameM.mutate(
                  { teamId: team.id, name: name.trim() },
                  { onSuccess: () => done("Команду перейменовано"), onError: fail },
                )
              }
            />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Create dialogs ─────────────────────────────────────────────────────────

function CreateDeptDialog({ onClose, onToast }: { onClose: () => void; onToast: (k: "ok" | "err", m: string) => void }) {
  const panelRef = useDrawerA11y(true, onClose);
  const [name, setName] = useState("");
  const [orgType, setOrgType] = useState<"sales_scope" | "functional_inbox">("sales_scope");
  const m = useOrgMutation("createDepartment");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5">
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label="Новий відділ" className="w-full max-w-sm rounded-card border bg-card p-4 shadow-xl outline-none">
        <h3 className="mb-3 text-sm font-semibold">Новий відділ</h3>
        <label className="mb-3 block">
          <span className="mb-1 block text-xs text-muted-foreground">Назва</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Відділ продажів" />
        </label>
        <div className="mb-3 space-y-1.5">
          <SelRow
            label="sales-scope — впливає на client-visibility"
            selected={orgType === "sales_scope"}
            onClick={() => setOrgType("sales_scope")}
          />
          <SelRow
            label="functional-inbox — впливає на routing заявок"
            selected={orgType === "functional_inbox"}
            onClick={() => setOrgType("functional_inbox")}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Скасувати
          </Button>
          <Button
            size="sm"
            disabled={m.isPending || name.trim().length < 2}
            onClick={() =>
              m.mutate(
                { name: name.trim(), orgType },
                {
                  onSuccess: () => {
                    onToast("ok", `Відділ «${name.trim()}» створено`);
                    onClose();
                  },
                  onError: (e) => onToast("err", e instanceof Error ? e.message : "Помилка"),
                },
              )
            }
          >
            {m.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Створити
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreateTeamDialog({
  departments,
  presetDeptId,
  onClose,
  onToast,
}: {
  departments: OrgDepartment[];
  presetDeptId: string | null;
  onClose: () => void;
  onToast: (k: "ok" | "err", m: string) => void;
}) {
  const panelRef = useDrawerA11y(true, onClose);
  const [name, setName] = useState("");
  const [deptId, setDeptId] = useState<string | null>(presetDeptId);
  const m = useOrgMutation("createTeam");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5">
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label="Нова команда" className="w-full max-w-sm rounded-card border bg-card p-4 shadow-xl outline-none">
        <h3 className="mb-3 text-sm font-semibold">Нова команда</h3>
        <label className="mb-3 block">
          <span className="mb-1 block text-xs text-muted-foreground">Назва</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Sales-Mgr-C" />
        </label>
        <p className="mb-1 text-xs text-muted-foreground">Відділ (1:1, опц.)</p>
        <div className="mb-3 max-h-[30vh] space-y-1.5 overflow-y-auto">
          {departments.map((d) => (
            <SelRow key={d.id} label={d.name} selected={deptId === d.id} onClick={() => setDeptId(d.id)} />
          ))}
          <SelRow label="Без відділу (привʼязати пізніше)" selected={deptId === null} onClick={() => setDeptId(null)} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Скасувати
          </Button>
          <Button
            size="sm"
            disabled={m.isPending || name.trim().length < 2}
            onClick={() =>
              m.mutate(
                { name: name.trim(), departmentId: deptId },
                {
                  onSuccess: () => {
                    onToast("ok", `Команду «${name.trim()}» створено`);
                    onClose();
                  },
                  onError: (e) => onToast("err", e instanceof Error ? e.message : "Помилка"),
                },
              )
            }
          >
            {m.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Створити
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── shared ─────────────────────────────────────────────────────────────────

function SelRow({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        "block w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors",
        selected ? "border-primary bg-primary-soft" : "hover:bg-accent",
      )}
    >
      {label}
    </button>
  );
}

function DrawerSave({
  busy,
  onCancel,
  onSave,
  disabledReason = null,
}: {
  busy: boolean;
  onCancel: () => void;
  onSave: () => void;
  disabledReason?: string | null;
}) {
  return (
    <div className="mt-4 border-t pt-3.5">
      {disabledReason && (
        <p className="mb-2 rounded bg-destructive-soft px-2 py-1.5 text-xs text-destructive">{disabledReason}</p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Назад
        </Button>
        <Button size="sm" disabled={busy || !!disabledReason} onClick={onSave}>
          {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Зберегти
        </Button>
      </div>
    </div>
  );
}
